/**
 * Defensive parse-boundary helpers (WSRADM-0003; handoff §9).
 *
 * Backend JSON enters the app through these small, tolerant narrowers so the
 * strict-TS settings (`noUncheckedIndexedAccess`, `exactOptionalPropertyTypes`)
 * stay workable: untyped input is narrowed ONCE, at the edge, and every later
 * line operates on a known shape. dink-admin defines an equivalent set privately
 * inside each `api/<resource>.ts` module; wsr-admin has no domain modules yet, so
 * the generic helpers live here in one shared `src/api/` module for every later
 * feature spec (WSRADM-0004+) to reuse rather than re-declare.
 *
 * **Never throw on a bad row.** A row-level parser built on these drops a
 * malformed entry (returns `null` for it) and keeps the good ones — a single bad
 * record from the backend can never blow up a whole list read. The helpers
 * themselves are total: they return `null` (or the bare-array/`null` for
 * {@link extractArray}) for any unexpected input rather than raising.
 */

/** True for a non-null object (arrays included — callers array-check first). */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed, non-empty string, or `null`. */
export function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string: entity ids often arrive as
 * numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else `null`. A
 * numeric id is never dropped; a genuinely-string id is preserved.
 */
export function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/**
 * A finite number from a real number or a numeric string, or `null`. `NaN` /
 * `Infinity` and non-numeric strings degrade to `null` rather than propagating a
 * bad value.
 */
export function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * A real JSON boolean, or the lowercase strings `"true"` / `"false"`. Anything
 * else (including `"True"`, `1`/`0`, and missing values) is `null` — callers
 * that require a boolean drop the row.
 */
export function asBoolean(value: unknown): boolean | null {
  if (value === true || value === false) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return null;
}

/**
 * Extract an array TOLERANTLY from a successful body: a bare array is returned as
 * is; otherwise the value at `key` is returned when it is an array (the
 * `{ key: [...] }` wrapper shape). Returns `null` when NEITHER yields an array —
 * the caller decides what `null` means (a list read turns it into `[]`, a
 * fail-closed pick-list turns it into a {@link import('./picklists').PickListError}).
 */
export function extractArray(body: unknown, key: string): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body) && Array.isArray(body[key])) return body[key];
  return null;
}
